import type { DayResponse, IsoDay } from '../api/client.ts';
import { dayAndMonth, weekdayName } from '../iso-day.ts';
import {
  dayLine,
  dayProgress,
  emptyDayBody,
  emptyDayTitle,
  pct,
  scheduledLine,
} from './day-model.ts';
import { BrandMark, Icon } from './Icon.tsx';
import { SectionCard } from './SectionCard.tsx';
import { AccountButton } from '../shell/NavBar.tsx';
import { useVoice, wording } from '../shell/voice.ts';
import type { Voice } from '../shell/voice.ts';
import { RemindersCard } from '../reminders/RemindersCard.tsx';

export type DayChoice = 'yesterday' | 'today' | 'tomorrow';

const CHOICES: readonly { key: DayChoice; label: string }[] = [
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
];

interface Props {
  /** Null while the day is in flight, and while it could not be read. */
  day: DayResponse | null;
  /** The day being looked at, known before the payload arrives. */
  dayKey: IsoDay | null;
  /** Whether there is a day to move from, which is all the chooser needs. */
  ready: boolean;
  /** Whether the day being looked at is on its way. */
  loading: boolean;
  /** Set only when the day on screen could not be read. */
  failure: string | null;
  onRetry: () => void;
  /** True when the day being looked at is after the server's today. */
  future: boolean;
  choice: DayChoice;
  onChoose: (choice: DayChoice) => void;
  folded: ReadonlySet<string>;
  openItems: ReadonlySet<string>;
  onToggleFold: (sectionId: string) => void;
  onToggleItem: (itemId: string) => void;
  onToggleStep: (itemId: string, stepId: string) => void;
  onToggleOpen: (itemId: string) => void;
  /** Ticks a reminder in the block above the schedules. */
  onToggleReminder: (reminderId: string) => void;
  /** Reminders ticked in this sitting, kept on screen so the tick is undoable. */
  pinnedReminders: ReadonlySet<string>;
  /** Opens the full reminders list, which is a screen and not a tab. */
  onOpenReminders: () => void;
  onEditReminder: (reminderId: string) => void;
  onEditSection: (sectionId: string) => void;
  onEditItem: (itemId: string, meta: string) => void;
  onAdd: () => void;
  /** The account button's destination. See the top row below. */
  onSettings: () => void;
  /**
   * The month around this day. Month is not a tab, so the control in the date
   * header below is the only way to it - see the note there.
   */
  onMonth: () => void;
}

export function DayScreen({
  day,
  dayKey,
  ready,
  loading,
  failure,
  onRetry,
  future,
  choice,
  onChoose,
  folded,
  openItems,
  onToggleFold,
  onToggleItem,
  onToggleStep,
  onToggleOpen,
  onToggleReminder,
  pinnedReminders,
  onOpenReminders,
  onEditReminder,
  onEditSection,
  onEditItem,
  onAdd,
  onSettings,
  onMonth,
}: Props) {
  const voice = useVoice();
  // The current API requires a string; the second `?.` also lets a client loaded
  // before a rolling server update greet naturally until the new field arrives.
  const name = day?.name?.trim() ?? '';

  return (
    <div className="wrap">
      {/* `brandonly` because both halves of this row belong to the phone: at
          sidebar width the sidebar carries the wordmark and its own Settings
          entry, so the whole row goes rather than leaving an empty band above
          the date - which is a defect the prototype found and fixed by looking
          at it (prototype-v1.md section 9). */}
      <div className="toprow brandonly">
        <span className="tr-brand">
          <BrandMark />
          <span>Perfect Note</span>
        </span>
        <AccountButton onClick={onSettings} />
      </div>

      {/* The only day navigation there is: no week strip, no calendar grid, no
          date picker. Three days is what a daily planner is looked at through.

          The brand, the chooser and the date stay mounted whatever the body is
          doing: the chooser is the only way back to a day already read, so a
          neighbouring day that is slow or unreachable must never take it away.
          Until the server has said which day today is there is no day to move
          from, so the chooser is disabled rather than moving a pill that
          nothing on screen answers to, no pill is drawn or announced as the
          chosen one, and there is no date to head the page with.

          Once it has, the chooser stays usable whatever the chosen day is
          doing - slow, unreachable, or never named by the payload at all. It is
          the only way back to a day already read, so the one state it must
          never reach is disabled while a day it cannot show is selected. */}
      <div className="seg" role="group" aria-label="Choose a day">
        {CHOICES.map((c) => (
          <button
            key={c.key}
            aria-pressed={ready ? choice === c.key : undefined}
            disabled={!ready}
            onClick={() => onChoose(c.key)}
          >
            {c.label}
          </button>
        ))}
      </div>

      {dayKey !== null && (
        <div className="dayheading">
          {/* One literal heading and one typography. The spans preserve the
              date's semantic parts while the heading applies the same uppercase
              serif treatment to both. Its whitespace is the accessible gap. */}
          <h1 className="dayhead">
            <span>{weekdayName(dayKey)}</span> <span>{dayAndMonth(dayKey)}</span>
          </h1>

          <div className="dayrow">
            {/* The authenticated profile's greeting name arrives with the day.
                Empty is a real answer, so each voice has a punctuation-safe
                anonymous line rather than borrowing from the email address. It
                is ordinary literal text: never an accessible label or live
                region, which the voice contract forbids changing. */}
            {day !== null && (
              <p className="daygreeting">
                {name === ''
                  ? wording('day.greetingAnon', voice)
                  : wording('day.greeting', voice, { name })}
              </p>
            )}

            {/* THE WAY TO THE MONTH, and the only one there is: Month is not a
                tab (`shell/screens.ts`), so this is the door. It stays in the
                line under the heading, beside the greeting. The chooser above
                reaches three days, and everything past them is behind this.

                It does NOT contradict the note on the chooser above. That rule is
                about what the DAY screen grows: no week strip, no grid, no date
                picker on this page. A link to another screen is not one of those,
                and the month it opens is a reading rather than a picker - it
                writes nothing and hands the day back through the same three
                choices this chooser offers.

                Drawn at EVERY width, deliberately. The top row above it is
                `brandonly` and disappears at sidebar width, so a control put
                there would exist on a phone and not on a desktop - which is this
                client's oldest defect wearing a new hat (web/src/README.md). The
                date is on every screen size, so this is. */}
            <button className="btn" onClick={onMonth}>
              <Icon name="month" />
              Month
            </button>
          </div>
        </div>
      )}

      {day === null ? (
        // Whether a day is on its way is a state of its own, never inferred
        // from the absence of a reason it is not: a screen with no day, nothing
        // in flight and no way to ask again is the one state this must not
        // reach.
        <div className="daystate" role="status">
          {loading ? (
            <p>Loading your day…</p>
          ) : (
            <>
              <h3>Your day is not here.</h3>
              <p>{failure}</p>
              <button className="btn" onClick={onRetry}>
                Try again
              </button>
            </>
          )}
        </div>
      ) : (
        <DayBody
          day={day}
          voice={voice}
          future={future}
          folded={folded}
          openItems={openItems}
          onToggleFold={onToggleFold}
          onToggleItem={onToggleItem}
          onToggleStep={onToggleStep}
          onToggleOpen={onToggleOpen}
          onToggleReminder={onToggleReminder}
          pinnedReminders={pinnedReminders}
          onOpenReminders={onOpenReminders}
          onEditReminder={onEditReminder}
          onEditSection={onEditSection}
          onEditItem={onEditItem}
          onAdd={onAdd}
        />
      )}
    </div>
  );
}

type BodyProps = Omit<
  Props,
  | 'day'
  | 'dayKey'
  | 'ready'
  | 'loading'
  | 'failure'
  | 'onRetry'
  | 'choice'
  | 'onChoose'
  | 'onSettings'
  | 'onMonth'
> & {
  day: DayResponse;
  voice: Voice;
};

function DayBody({
  day,
  voice,
  future,
  folded,
  openItems,
  onToggleFold,
  onToggleItem,
  onToggleStep,
  onToggleOpen,
  onToggleReminder,
  pinnedReminders,
  onOpenReminders,
  onEditReminder,
  onEditSection,
  onEditItem,
  onAdd,
}: BodyProps) {
  const progress = dayProgress(day.sections);
  const hasSections = day.sections.length > 0;
  // The wording, for the three sentences on this screen that have one - each
  // picked in `day-model.ts`, which is also where the one rule about WHEN the
  // empty day's casual heading may be used lives. Every other string here - the
  // day choices, the counts, the percentage, the add button - is operational and
  // identical in both voices (`shell/voice.ts`). The voice is supplied by the
  // screen above so the greeting and the body always read from one choice.
  // The block is drawn by the payload having reminders on this day, and by
  // nothing else. A future day carries none - a day that has not arrived is not
  // a day anything was open on (api/src/domain/day.ts) - so "no reminders on a
  // day ahead" needs no rule here and cannot be got wrong here either.
  const hasReminders = day.reminders.length > 0;

  // Built once and placed twice: it sits above the schedules on an ordinary
  // day, and BELOW the empty panel on a day with nothing scheduled, which is
  // the approved arrangement (prototype-v2.md section 1.2). Two copies of the
  // element would be two places for its props to drift apart.
  const reminders = hasReminders ? (
    <RemindersCard
      reminders={day.reminders}
      today={day.today}
      pinned={pinnedReminders}
      onToggle={onToggleReminder}
      onEdit={onEditReminder}
      onOpenAll={onOpenReminders}
    />
  ) : null;

  return (
    <>
      {hasSections &&
        (future ? (
          // A future day is a plan, not a record: counts only, and no bar
          // anywhere on it. Filling a bar for work nobody has done yet reads as
          // failure rather than as ahead.
          <div className="prog">
            <div className="prog-row">
              <span className="prog-n tnum">{scheduledLine(progress.total)}</span>
            </div>
            <div className="dayline">{dayLine(day.sections, true, voice)}</div>
          </div>
        ) : (
          <div className="prog">
            <div className="prog-row">
              <span className="prog-n tnum">
                {progress.done} of {progress.total} done
              </span>
              <span className="prog-pct tnum">{pct(progress)}%</span>
            </div>
            <div className="bar">
              <i style={{ width: `${pct(progress)}%` }} />
            </div>
            <div className="dayline">{dayLine(day.sections, false, voice)}</div>
          </div>
        ))}

      {hasSections ? (
        <div className="sections">
          {/* Above the schedules, because a reminder is the thing with no home
              that gets forgotten - and the block is capped at two rows so it
              cannot push the first schedule's header below the fold. */}
          {reminders}
          {day.sections.map((section) => (
            <SectionCard
              key={section.id}
              section={section}
              future={future}
              folded={folded.has(section.id)}
              openItems={openItems}
              onToggleFold={onToggleFold}
              onToggleItem={onToggleItem}
              onToggleStep={onToggleStep}
              onToggleOpen={onToggleOpen}
              onEditSection={onEditSection}
              onEditItem={onEditItem}
            />
          ))}
        </div>
      ) : (
        <>
          {/* `compact` when there are reminders under it: a day with nothing
              scheduled but three things to remember is not an empty screen, and
              a panel claiming half the viewport above them says it is. */}
          <div className={hasReminders ? 'empty compact' : 'empty'}>
            <h3>{emptyDayTitle(day.day, day.today, voice)}</h3>
            <p>{emptyDayBody(day.day, voice)}</p>
            <button className="btn primary" onClick={onAdd}>
              <Icon name="plus" />
              Add something
            </button>
          </div>
          {reminders !== null && <div className="sections">{reminders}</div>}
        </>
      )}
    </>
  );
}
